import React, { useState, useEffect } from 'react';
import { AgencyService } from '../../services/supabase/agency.service';
import { useAuth } from '../../context/AuthContext';

interface AgencyLogoProps {
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showFallback?: boolean;
}

export const AgencyLogo: React.FC<AgencyLogoProps> = ({
  className = '',
  size = 'medium',
  showFallback = true
}) => {
  const { userProfile } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Définir les tailles en fonction de la prop
  const sizeClasses = {
    small: 'h-6 w-12',      // 24px x 48px (réduit mais visible)
    medium: 'h-8 w-16',     // 32px x 64px (réduit)
    large: 'h-10 w-20'      // 40px x 80px (réduit)
  };

  useEffect(() => {
    const fetchAgencyLogo = async () => {
      if (!userProfile?.agency_id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const agency = await AgencyService.getAgencyWithLogo(userProfile.agency_id);
        
        if (agency?.logo_url) {
          setLogoUrl(agency.logo_url);
        }
      } catch (err) {
        console.error('Erreur lors du chargement du logo:', err);
        setError('Impossible de charger le logo');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgencyLogo();
  }, [userProfile?.agency_id]);

  // Gérer les erreurs de chargement d'image
  const handleImageError = () => {
    setError('Logo non disponible');
    setLogoUrl(null);
  };

  // Logo par défaut (texte)
  const DefaultLogo = () => (
    <div className={`
      flex items-center justify-center font-bold text-gray-700
      ${sizeClasses[size]}
      ${className}
    `}>
      IP
    </div>
  );

  // État de chargement
  if (isLoading) {
    return (
      <div className={`
        ${sizeClasses[size]}
        ${className}
        bg-gray-200 animate-pulse rounded
      `} />
    );
  }

  // Afficher le logo si disponible
  if (logoUrl && !error) {
    return (
      <img
        src={logoUrl}
        alt="Logo de l'agence"
        className={`
          ${sizeClasses[size]}
          ${className}
          object-contain transition-opacity duration-200
        `}
        onError={handleImageError}
        loading="lazy"
      />
    );
  }

  // Fallback si pas de logo ou erreur
  if (showFallback) {
    return <DefaultLogo />;
  }

  // Ne rien afficher si pas de fallback
  return null;
};

// Composant simplifié pour le header
export const HeaderAgencyLogo: React.FC<{ className?: string; size?: 'small' | 'medium' | 'large' }> = ({ 
  className = '', 
  size = 'medium' // Taille normale pour desktop, sera réduit sur mobile via CSS
}) => {
  return (
    <AgencyLogo
      size={size}
      className={`transition-all duration-200 hover:opacity-80 ${className}`}
      showFallback={true}
    />
  );
};

// Composant pour la carte de profil
export const ProfileAgencyLogo: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <AgencyLogo
      size="large"
      className={`border border-gray-200 rounded-lg shadow-sm ${className}`}
      showFallback={false}
    />
  );
};
